import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "./assets/index.css";
import { QueryClient } from "@tanstack/react-query";
import { get as getItem, set as setItem, del as removeItem } from "idb-keyval";
import { ConvexQueryClient } from "@convex-dev/react-query";
// Convex
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { registerSW } from "virtual:pwa-register";

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);
const convexQueryClient = new ConvexQueryClient(convex);

// Tanstack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
      networkMode: "always",
      staleTime: Infinity, // Keep data fresh indefinitely when offline
    },
    mutations: {
      networkMode: "always",
    },
  },
});

const asyncStoragePersister = createAsyncStoragePersister({
  storage: {
    getItem,
    setItem,
    removeItem,
  },
});

convexQueryClient.connect(queryClient);

// TanStack Router
// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Add this before your app initialization
if ("serviceWorker" in navigator) {
  registerSW({
    immediate: true,
    onNeedRefresh() {
      // Handle new content available
      console.log("New content available, please refresh");
    },
    onOfflineReady() {
      // Handle offline ready
      console.log("App ready to work offline");
    },
  });
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <ConvexProvider client={convex}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{ persister: asyncStoragePersister }}
        >
          <RouterProvider router={router} />
        </PersistQueryClientProvider>
      </ConvexProvider>
    </StrictMode>
  );
}
